import cn from "classnames";
import AverageRateInfo from "components/bid/averageRateInfo";
import DropdownBtn from "components/shared/dropdown";
import { Field, Flags, Select } from "components/shared/form";
import { counterpartyInsolvency } from "helpers/bid";
import { formatDateTimezone } from "helpers/bidpoint";
import { getRateComparisonMeta } from "helpers/bidRates";
import {
    bidPointOptionFlags,
    bidPointOptionFlagsEnum,
    bidPointTypes,
    bidPointTypesEnum,
    featureModuleTypesEnum,
} from "helpers/enums";
import { enumLabel, pointLetter } from "helpers/system";
import { phoneNumber as phoneValidator } from "helpers/validators";
import React, { Component } from "react";
import { deleteServicePoint, getBidContact } from "sources/books/bids";
import { list as scenariosList } from "sources/books/scenarios";
import AddressPicker from "./addressPicker";
import CustomPointType from "./customPointType";
import ForwardingOrder from "./forwardingOrder";
import PlanEnterDate from "./planEnterDate";
import PlanLeaveDate from "./planLeaveDate";

const CONSIGNEE_MESSAGES = {
    start: "Введите номер телефона в формате +7XXXXXXXXXX",
    load: "Проверка существования номера...",
    success: "Номер существует, Имя пользователя добавлено",
    error: "Номер не найден. Введите имя пользователя чтобы создать его.",
};

const hasRateValue = (value) =>
    value !== null &&
    value !== undefined &&
    !(typeof value === "string" && value.trim() === "") &&
    Number.isFinite(Number(value)) &&
    Number(value) > 0;

export { AddressPicker, CONSIGNEE_MESSAGES };

export default class Point extends Component {
    state = {
        isLoading: false,
        currentMessage: "",
    };

    constructor(props) {
        super(props);

        this.isCustomPoint = bidPointTypesEnum.CustomPoint === props.item.type;
    }

    setValue = ({ name, value, option }) => {
        const { onChange, i, isFirstOrLastItem } = this.props;
        onChange({ name, value }, i);
        if (["planEnterDate", "planLeaveDate"].includes(name) && isFirstOrLastItem) {
            this.props.onPointDatesChange();
        }
        if (name === "cargoOwnerDictionaryItemId") {
            this.props.updateCounterpartiesData(value, option);
        }
    };

    setConsigneeValue = ({ name, value }) => {
        if (name === "phoneNumber") {
            if (!phoneValidator(value)) {
                this.setState({ currentMessage: CONSIGNEE_MESSAGES.load, isLoading: true });
                getBidContact({ phoneNumber: value }).then((res) => {
                    if (res && res.name) {
                        this.setState({ currentMessage: CONSIGNEE_MESSAGES.success, isLoading: false });
                        this.setConsigneeValue({ name: "name", value: res.name });
                    } else {
                        this.setState({ currentMessage: CONSIGNEE_MESSAGES.error, isLoading: false });
                    }
                });
            } else {
                this.setState({ currentMessage: "", isLoading: true });
            }
        }
        this.setValue({ name: "contactPerson", value: { ...this.props.item.contactPerson, [name]: value } });
    };

    handleConsignee = () => {
        const { contactPerson } = this.props.item;
        if (contactPerson.phoneNumber && contactPerson.name) {
            this.setState({ currentMessage: CONSIGNEE_MESSAGES.success });
        } else if (contactPerson.phoneNumber) {
            this.setState({ currentMessage: CONSIGNEE_MESSAGES.error });
        }
    };

    renderOrderTopArrow = () => {
        const { i, reorder, availableFeatureModules } = this.props;
        if (
            this.isCustomPoint &&
            i === 1 &&
            !availableFeatureModules.includes(featureModuleTypesEnum.PlanEmptyMileageBid)
        ) {
            return null;
        }
        const cname = "book-form__section-arrow book-form__section-arrow--top";
        const action = () => reorder(i, i - 1);
        return i !== 0 && <span className={cname} onClick={action} title="Вверх в очереди" />;
    };

    renderOrderBottomArrow = () => {
        const { i, items, reorder } = this.props;
        if (this.isCustomPoint && i === items.length - 2) {
            return null;
        }
        const cname = "book-form__section-arrow book-form__section-arrow--bottom";
        const action = () => reorder(i, i + 1);
        return 1 + i < items.length && <span className={cname} onClick={action} title="Вниз в очереди" />;
    };

    currentMessage = () => {
        if (this.state.currentMessage.length) {
            return (
                <small className="text-info offset-sm-4 offset-md-3 col-sm-8 col-md-5">
                    {this.state.currentMessage}
                </small>
            );
        }
    };

    changePointOptions = (type) => {
        const types = bidPointTypes.filter(
            (item) =>
                item.value !== type &&
                [bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(item.value),
        );
        return types.map((item) => ({
            text: item.label,
            action: () => this.setValue({ name: "type", value: item.value }),
            show: true,
        }));
    };

    render() {
        const {
            bidId,
            modal,
            item: {
                id,
                pointIndex,
                contactPerson,
                geozone,
                planEnterDate,
                secondaryPlanEnterDate,
                type,
                client,
                comment,
                cargoOwnerDictionaryItemId,
                loadOptions,
                planLeaveDate,
                scenarioId,
                customPointTypeId,
                intOptions,
                planEnterDateOffset,
                hasLogistOrAutoDates,
                externalCity,
            },
            switchMap,
            i,
            remove,
            items,
            isEmptyMileageBid,
            inFinishedStatus,
            bidPolicy,
            counterpartiesData,
            bidCounterpartyId,
            carStatus,
            bidStatus,
            availableFeatureModules,
            couplingDates,
            firstLoadPointIndex,
            profile,
            organization,
            roles,
            forwardingOrder,
            forwardingOrderConfig,
            setForwardingData,
            workRestInfo,
            averageRateByDestination,
            averageRateDate,
            bidAverageRate,
        } = this.props;
        const utcTimezone = !!id && formatDateTimezone({ planEnterDateOffset });
        const isServicePoint = type === bidPointTypesEnum.ServicePoint;
        const isBidPoint = type !== bidPointTypesEnum.ServicePoint;
        const isDisabled = inFinishedStatus || !isBidPoint;
        const pointIconClassName = cn("book-form__section-head--point-icon", { "icon-uEA97-wrench": isServicePoint });
        const showAverageRateInfo =
            i === items.length - 1 && (hasRateValue(averageRateByDestination) || hasRateValue(bidAverageRate));

        const commentConfig = !isEmptyMileageBid
            ? { ...this.props.config.comment }
            : { ...this.props.config.comment, required: false, validators: [] };

        const removePoint = () => {
            return modal({
                name: "confirm",
                title: "Удаление точки",
                text: "Вы уверены, что хотите удалить точку маршрута?",
                action: () => {
                    let promise;
                    if (isServicePoint) {
                        promise = () => deleteServicePoint({ id, bidId });
                    }
                    return (promise ? promise() : Promise.resolve(true)).then((res) => {
                        if (res) {
                            remove(i);
                            setTimeout(this.props.onPointRemoved, 100);
                        }
                    });
                },
            });
        };
        return (
            <div>
                <div className="book-form__section-head book-form__section-head--closable">
                    {(!isDisabled || (isServicePoint && !hasLogistOrAutoDates)) && (
                        <div
                            className="book-form__section-close close book-form__section-close--point"
                            onClick={removePoint}
                            title="Удалить точку"
                        />
                    )}
                    <div className="form-group">
                        <div className={pointIconClassName}>
                            {isServicePoint ? null : pointLetter(pointIndex, firstLoadPointIndex)}
                        </div>
                        <strong>{enumLabel(bidPointTypes, type)}</strong>
                        {[bidPointTypesEnum.LoadPoint, bidPointTypesEnum.UnloadPoint].includes(type) && (
                            <span className="pl-3 b-point__type-dropdown--wrap">
                                <DropdownBtn
                                    text="Изменить"
                                    cname="b-point__type-dropdown link"
                                    items={this.changePointOptions(type)}
                                />
                            </span>
                        )}
                    </div>
                    {!isDisabled && (
                        <div className="book-form__section-arrows">
                            {this.renderOrderTopArrow()}
                            {this.renderOrderBottomArrow()}
                        </div>
                    )}
                </div>
                <div>
                    {!isEmptyMileageBid &&
                        !client &&
                        !this.isCustomPoint &&
                        isBidPoint &&
                        this.props.config.cargoOwnerDictionaryItemId && (
                            <Select
                                {...this.props.config.cargoOwnerDictionaryItemId}
                                wrapper="book-field"
                                fieldWrapper="col-sm-8 col-md-5"
                                onChange={this.setValue}
                                value={cargoOwnerDictionaryItemId}
                                index={i}
                                disabled={isDisabled}
                                modal={modal}
                                organization={organization}
                                profile={profile}
                                roles={roles}
                                label={type === bidPointTypesEnum.LoadPoint ? "Грузоотправитель" : "Грузополучатель"}
                                inputBottomText={
                                    <>
                                        <div>
                                            {counterpartyInsolvency(
                                                counterpartiesData[cargoOwnerDictionaryItemId]?.insolvencyRegistryEntry,
                                            )}
                                        </div>
                                        <small>
                                            <i className="text-muted">
                                                {counterpartiesData[cargoOwnerDictionaryItemId]?.comment}
                                            </i>
                                        </small>
                                    </>
                                }
                            />
                        )}
                    {this.isCustomPoint && (
                        <CustomPointType
                            {...{
                                i,
                                isDisabled,
                                value: customPointTypeId,
                                planLeaveDate,
                                planEnterDate,
                                isEmptyMileageBid,
                                onChange: this.setValue,
                            }}
                        />
                    )}
                    <AddressPicker
                        {...{
                            isDisabled,
                            i,
                            counterpartiesData,
                            bidPointType: type,
                            geozone,
                            switchMap,
                            bidCounterpartyId,
                            cargoOwnerDictionaryItemId,
                            onChange: this.setValue,
                            modal: this.props.modal,
                            inFinishedStatus,
                            availableFeatureModules,
                        }}
                    />
                    {showAverageRateInfo && (
                        <div className="row form-group">
                            <div className="col-sm-8 col-md-5 offset-sm-4 offset-md-3">
                                <AverageRateInfo
                                    averageRateByDestination={averageRateByDestination}
                                    averageRateDate={averageRateDate}
                                    bidAverageRate={bidAverageRate}
                                    comparison={getRateComparisonMeta({
                                        averageRateByDestination,
                                        averageRate: bidAverageRate,
                                    })}
                                />
                            </div>
                        </div>
                    )}
                    <ForwardingOrder
                        isLastPoint={items.length === i + 1}
                        addressSelected={!!geozone.address}
                        profile={profile}
                        forwardingOrder={forwardingOrder}
                        forwardingOrderConfig={forwardingOrderConfig}
                        setForwardingData={setForwardingData}
                    />
                    <PlanEnterDate
                        {...{
                            config: this.props.config,
                            i,
                            utcTimezone,
                            isDisabled,
                            planEnterDate,
                            secondaryPlanEnterDate,
                            carStatus,
                            bidStatus,
                            onChange: this.setValue,
                            couplingDates,
                        }}
                    />
                    {!isEmptyMileageBid && (
                        <PlanLeaveDate
                            {...{
                                availableFeatureModules,
                                isDisabled,
                                utcTimezone,
                                type,
                                i,
                                value: planLeaveDate,
                                onChange: this.setValue,
                                planEnterDate,
                                workRestInfo,
                            }}
                        />
                    )}
                    {!isEmptyMileageBid && !this.isCustomPoint && !isServicePoint && (
                        <Select
                            {...this.props.config.loadOptions}
                            isMulti
                            wrapper="book-field"
                            fieldWrapper="col-sm-8 col-md-5"
                            inputProps={{
                                name: "loadOptions",
                            }}
                            options={this.props.enums.loadUnloadType}
                            onChange={({ name, value }) => this.setValue({ name, value: value.map((id) => ({ id })) })}
                            value={(loadOptions || []).map((option) => option.id)}
                            index={i}
                            disabled={isDisabled}
                            label={type === bidPointTypesEnum.LoadPoint ? "Типы погрузки" : "Типы выгрузки"}
                        />
                    )}
                    {!this.isCustomPoint && bidPolicy.canSeeScenarios() && (
                        <Select
                            wrapper="book-field"
                            fieldWrapper="col-sm-8 col-md-5"
                            inputProps={{
                                name: "scenarioId",
                            }}
                            propName="name"
                            url={scenariosList}
                            onChange={this.setValue}
                            value={scenarioId}
                            index={i}
                            disabled={!isBidPoint}
                            label="Сценарий"
                        />
                    )}
                    {commentConfig && isBidPoint && (
                        <Field
                            textarea
                            {...commentConfig}
                            fieldWrapper="col-sm-8 col-md-5"
                            label="Комментарий"
                            inputProps={{
                                name: "comment",
                            }}
                            onChange={this.setValue}
                            value={comment}
                            disabled={isDisabled}
                            index={i}
                        />
                    )}
                    {!isEmptyMileageBid && client && !this.isCustomPoint && (
                        <Field
                            fieldWrapper="col-sm-8 col-md-5"
                            label="Клиент"
                            inputProps={{
                                name: "client",
                            }}
                            onChange={this.setValue}
                            value={client}
                            disabled={isDisabled}
                            index={i}
                        />
                    )}
                    {!isEmptyMileageBid && this.props.config.contactPerson && isBidPoint && (
                        <>
                            <div className="form-group">
                                <Field
                                    {...this.props.config.contactPerson}
                                    label="Мобильный телефон"
                                    fieldWrapper="col-sm-8 col-md-5"
                                    bWrapper="row"
                                    inputProps={{
                                        name: "phoneNumber",
                                    }}
                                    onChange={this.setConsigneeValue}
                                    value={contactPerson ? contactPerson.phoneNumber : ""}
                                    disabled={isDisabled}
                                    index={i}
                                />
                                {this.currentMessage()}
                            </div>
                            <Field
                                {...this.props.config.contactPerson}
                                label="Контактное лицо"
                                inputProps={{
                                    name: "name",
                                }}
                                fieldWrapper="col-sm-8 col-md-5"
                                onChange={this.setConsigneeValue}
                                value={contactPerson ? contactPerson.name : ""}
                                disabled={isDisabled}
                                index={i}
                            />
                        </>
                    )}
                    {!isEmptyMileageBid && (
                        <Field
                            fieldWrapper="col-sm-8 col-md-5"
                            {...this.props.config.externalCity}
                            onChange={this.setValue}
                            value={externalCity}
                            disabled={isDisabled}
                            index={i}
                        />
                    )}
                    {!isEmptyMileageBid && items.length === i + 1 && (
                        <Flags
                            label="Опции точки маршрута"
                            onChange={this.setValue}
                            flags={bidPointOptionFlags.filter(
                                (item) =>
                                    ![
                                        bidPointOptionFlagsEnum.EmptyMileagePoint,
                                        bidPointOptionFlagsEnum.EstimatedDateAsPlanned,
                                    ].includes(item.value),
                            )}
                            inputProps={{ name: "intOptions" }}
                            value={intOptions || 0}
                            index={i}
                            disabled={isDisabled}
                            fieldWrapper="col-sm-8 col-md-5"
                            tooltip='Установка флага означает, что выгрузка не начнется раньше времени, указанном в поле "Выгрузка начнется в" независимо от времени приезда машины на выгрузку'
                        />
                    )}
                </div>
            </div>
        );
    }
}
